import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";

import { CellEditingStoppedEvent, ColDef, PostSortRowsParams, RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { defaultKSK2030_R01Dto, KSK2030_R01Dto } from "./Dto_KSK2030_R01";
import { useKSK2030_R01Store } from "./Store_KSK2030_R01";
import { getItemPopupList } from "src/apis/itemApi";
import { headerNames_KSK2030_R01, isDuplicateChildItemcd, restoreOriginalValues, validateItemAccount } from './Util_KSK2030_R01';
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/useDetailTableEvents";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { CommonCodeColumn, CompCdColumn, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	// hoveredRowIndex: number | null;
	// setHoveredRowIndex: (hoveredRowIndex: number | null) => void;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<KSK2030_R01Dto> | null>;
	fetchOne: (data: KSK2030_R01Dto ) => void;
	fetchDetailList: (productWorkNo: string) => void;
}

const COLUMN_STATE_KEY = 'KSK2030_R01-grid-column-state';

export const Table_KSK2030_R01 = (props: Props) => {

	const { 
		// hoveredRowIndex, setHoveredRowIndex,
		clickable, setClickable,
		gridRef,
		fetchOne,
		fetchDetailList,
	} = props;

    const { 
		KSK2030_R01List, setKSK2030_R01List,
		insertKSK2030_R01List,
		removeKSK2030_R01List,
		updateKSK2030_R01List,
		selectedKSK2030_R01, setSelectedKSK2030_R01, initSelectedKSK2030_R01,
		deletedKSK2030_R01List, removeDeletedKSK2030_R01List, addDeletedKSK2030_R01List,
        columnState, setColumnState,
		originalKSK2030_R01List, setOriginalKSK2030_R01List,
		KSK2030_R01DetailList, setKSK2030_R01DetailList,
		modalVisible, setModalVisible,
		isDisabled,
	} = useKSK2030_R01Store();

    const { compCdList, jobTeamList, orgCdList, ordTypeList, workProcessList, workPlaceList, unitList } = useCommonCodeStore();

	// const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent<KSK2030_R01Dto>({
		gridRef,
		key: 'productResultNo',
		gridData: KSK2030_R01List,
		defaultGridData: defaultKSK2030_R01Dto,
		selectedRow: selectedKSK2030_R01,
	});

	const workPlaceListToUse = useMemo(() => {
		return workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');
	}, [workPlaceList]);

	const getCellStyle = (params: any, key: string) => {
		return onlyTouchGridStyle(
			key,
			'productOrderNo',
		)(params);
	}
	

	const baseColumnDefs: ColDef<KSK2030_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '비가동설정', colId: 'downtimeSet', field: 'downtimeSet', width: 120, 
			editable: false,
			cellRenderer: (params: any) => {
				// 하단 합계 행(pinned row)인 경우 렌더링 하지 않음
				if (params.node.rowPinned) return null;

				return (
					<div className="w-full h-full flex items-center justify-center">
						<button
							className="w-full h-full flex items-center justify-center bg-gray-300 text-white text-[18px] rounded hover:bg-gray-600 border border-gray-600"
							onClick={(e) => {
								e.stopPropagation(); // 행 클릭 이벤트 전파 방지
								fetchOne(params.data as KSK2030_R01Dto);
								setModalVisible(true);
							}}
						>
							설정
						</button>
					</div>
				);
			}
		},
		{
			headerName: '작업지시번호', colId: 'productOrderNo', field: 'productOrderNo', width: 150, 
			cellStyle: (params: any) => getCellStyle(params, 'productOrderNo'),
			editable: false,
		},
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 150, 
			cellStyle: (params: any) => getCellStyle(params, 'productResultNo'),
			editable: false,
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			cellStyle: (params: any) => getCellStyle(params, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '작업일자', colId: 'workDt', field: 'workDt', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'workDt'),
			valueFormatter: (params: any) => params.data?.workDt ? formatDate(new Date(params.data?.workDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '지시구분', colId: 'orderType', field: 'orderType', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderType'),
			cellEditor: 'agSelectCellEditor', 
            cellEditorParams: { 
				values: ordTypeList.map(opt => String(opt.minorCd)) 
			}, 
            valueFormatter: (params: any) => { 
				const codeNm = ordTypeList.find(opt => String(opt.minorCd) === String(params.value)); 
				return codeNm ? codeNm.codeNm : params.value; 
			}
		},
		{
			headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'workplaceCd'),
			...WorkPlaceColumn.options(
				workPlaceListToUse, false, (params: any) => params.data?.compCd, undefined, undefined
			)
		},
		{
			headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'workProcess'),
			...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
		},
		{
			headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'jobTeam'),
			cellEditor: 'agSelectCellEditor', 
			cellEditorParams: { 
				values: jobTeamList.map(opt => String(opt.minorCd)) 
			}, 
            valueFormatter: (params: any) => { 
				const codeNm = jobTeamList.find(opt => String(opt.minorCd) === String(params.value)); 
				return codeNm ? codeNm.codeNm : params.value; 
			}
		},
		{
			headerName: '작업자', colId: 'productEmp', field: 'productEmp', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'productEmp'),
		},
		{
			headerName: '작업자명', colId: 'productEmpNm', field: 'productEmpNm', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'productEmpNm'),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemSpec'),
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'itemUnit'),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '지시량', colId: 'orderQty', field: 'orderQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '생산량', colId: 'resultQty', field: 'resultQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'resultQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '불량', colId: 'badQty', field: 'badQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'resultQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '실적량', colId: 'acceptQty', field: 'acceptQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'resultQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '불량률(%)', colId: 'lossPercent', field: 'lossPercent', width: 110,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'lossPercent'),
			valueGetter: (params: any) => {
				const orderQty = Number(params.data?.orderQty) || 0;
				const badQty = Number(params.data?.badQty) || 0;
				if (orderQty <= 0) return 0;
				return (badQty / orderQty) * 100;
			},
			valueFormatter: (params: any) =>
				params.value != null ? `${Number(params.value).toFixed(2)}` : '0.00',
		},
		

		// ...IndexParameterCell(),

	], [hoveredRowIndex, deletedKSK2030_R01List, KSK2030_R01List, orgCdList, compCdList, workPlaceList, jobTeamList, ordTypeList, workProcessList]);



	const columnDefs: ColDef<KSK2030_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK2030_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_KSK2030_R01,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames_KSK2030_R01]);


	const handleRowClick = useCallback((e: RowClickedEvent<KSK2030_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.valueIndex;
		const selectedKey = selectedKSK2030_R01?.valueIndex;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedKSK2030_R01(undefined);
			setKSK2030_R01DetailList([]);
		} else {
			setSelectedKSK2030_R01(rowData);
			fetchDetailList(rowData.productResultNo);
		}
	}, [selectedKSK2030_R01, setSelectedKSK2030_R01]);

	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<KSK2030_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		//모달 열기
		fetchOne(rowData);
		setModalVisible(true);
	}, [fetchOne, setModalVisible]);

	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			productOrderNo: KSK2030_R01List.length.toLocaleString() + "건",
			orderQty: KSK2030_R01List.reduce((sum, account) => sum + (deletedKSK2030_R01List.includes(account.productResultNo) ? 0 : Number(account.orderQty) || 0), 0).toLocaleString(),
			resultQty: KSK2030_R01List.reduce((sum, account) => sum + (deletedKSK2030_R01List.includes(account.productResultNo) ? 0 : Number(account.resultQty) || 0), 0).toLocaleString(),
			editable: false,
		}
	]), [KSK2030_R01List]);

	//+- 버튼 테이블에는 보류(조회하면 select한 행을 초기화)
	// useEffect(() => { ensureSelectedRow(); }, [KSK2030_R01List, selectedKSK2030_R01List]);

    return (
		<GridContextWrapper<KSK2030_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedKSK2030_R01}
			defaultRowData={defaultKSK2030_R01Dto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<KSK2030_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}			defaultColDef={{
					...defaultColDef

				}}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<KSK2030_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedKSK2030_R01 ? [selectedKSK2030_R01.valueIndex] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
			/>
		</GridContextWrapper>
	)
}